/* eslint-disable eqeqeq */
import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom"; 
import 'react-toastify/dist/ReactToastify.css';
import api from "../api"; 

const UserRegistrationPage = () => {
  const navigate = useNavigate();
  
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // --- NEW: Initialize and Validate Project ID on Mount ---
  useEffect(() => {
    const initializeProjectContext = () => {
      // 1. Get User Data from Local Storage
      const currentUserStr = localStorage.getItem("currentUser");
      if (!currentUserStr) {
        toast.error("User session not found. Please login.");
        navigate("/");
        return;
      }

      const currentUser = JSON.parse(currentUserStr);
      const userProjects = currentUser.projects || [];

      // 2. Validate User has Projects Assigned
      if (userProjects.length === 0) {
        toast.error("No projects assigned to your account.");
        return;
      }

      // 3. Determine and Validate the Active Project ID
      let currentProjectId = localStorage.getItem("selectedProjectId");

      // Clean up bad storage values or set default
      if (!currentProjectId || currentProjectId === "undefined" || currentProjectId === "null") {
        currentProjectId = userProjects[0].projectId;
        localStorage.setItem("selectedProjectId", currentProjectId);
      }

      // SECURITY CHECK: Ensure the Project ID actually belongs to this user
      // (Using == here allows matching string "101" from storage with number 101 from API)
      const isProjectValidForUser = userProjects.some(p => p.projectId == currentProjectId);

      if (!isProjectValidForUser) {
        // Fallback to the user's first valid project
        currentProjectId = userProjects[0].projectId;
        localStorage.setItem("selectedProjectId", currentProjectId);
        console.log("Project ID auto-corrected to user's assigned project.");
      }
    };

    initializeProjectContext();
  }, [navigate]);
  // --------------------------------------------------------

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const projectId = localStorage.getItem("selectedProjectId");
    
    // Double check just in case
    if (!projectId) {
      toast.error("No project is selected. Please go back and select a project first.");
      setIsLoading(false);
      return;
    }

    const newUserPayload = {
      projectId: parseInt(projectId, 10),
      email,
      name,
      phone,
    };

    try {
      const response = await api.post("/vres/users", newUserPayload);
      toast.success(response.data.message || "User registered to project successfully!");
      handleCancel(); 
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || "An unknown error occurred.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setEmail("");
    setName("");
    setPhone("");
  };

  // --- STYLES INJECTION ---
  const PageStyleBlock = () => (
    <style>{`
      .ur-input:focus {
          border-color: #20c997 !important;
          box-shadow: 0 0 0 3px rgba(32, 201, 151, 0.1) !important;
          outline: none;
      }
      .ur-btn:hover:not(:disabled) { opacity: 0.9; }
      .ur-btn:disabled { opacity: 0.6; cursor: not-allowed; }
    `}</style>
  );

  return (
    <div style={styles.formWrapper}>
      <PageStyleBlock />
      
      <div style={styles.formCard}>
        <h2 style={styles.formTitle}>User Registration</h2>

        <form onSubmit={handleSubmit}>
          <div style={styles.formGroup}>
            <label htmlFor="email" style={styles.formLabel}>Email (User ID)</label>
            <input
              type="email"
              id="email"
              className="ur-input"
              style={styles.formControl}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="name" style={styles.formLabel}>Name</label>
            <input
              type="text"
              id="name"
              className="ur-input"
              style={styles.formControl}
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div style={styles.formGroup}>
            <label htmlFor="phone" style={styles.formLabel}>Phone</label>
            <input
              type="tel"
              id="phone"
              className="ur-input"
              style={styles.formControl}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
          </div>

          <div style={styles.btnGroup}>
            <button
              type="button"
              onClick={handleCancel}
              className="ur-btn"
              style={{ ...styles.btn, ...styles.btnSecondary }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="ur-btn"
              style={{ ...styles.btn, ...styles.btnPrimary }}
            >
              {isLoading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- STYLES (Matched to RegisterCoordinator & InitiateProject) ---
const styles = {
  formWrapper: {
    padding: '20px',
    paddingTop: '60px', 
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center', 
    width: '100%',
    minHeight: 'calc(100vh - 70px)', 
    boxSizing: 'border-box',
    fontFamily: "'Segoe UI', Roboto, sans-serif",
    backgroundColor: '#f0fdf9', 
  },
  formCard: {
    background: 'white',
    width: '100%',
    maxWidth: '500px',
    padding: '30px 40px', 
    borderRadius: '12px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
    borderTop: '5px solid #20c997', 
    margin: '0 auto',
  },
  formTitle: {
    textAlign: 'center',
    marginBottom: '25px',
    fontSize: '1.4rem',
    color: '#007bff', 
    fontWeight: '700',
    marginTop: 0,
  },
  formGroup: {
    marginBottom: '20px',
  },
  formLabel: {
    display: 'block',
    marginBottom: '6px',
    fontWeight: '600',
    color: '#2d3436', 
    fontSize: '0.9rem',
  },
  formControl: {
    width: '100%',
    padding: '10px 12px', 
    border: '1px solid #dcdcdc',
    borderRadius: '6px',
    fontSize: '0.9rem',
    transition: 'all 0.3s',
    outline: 'none',
    boxSizing: 'border-box',
    color: '#2d3436',
    backgroundColor: '#fff',
  },
  btnGroup: {
    display: 'flex',
    gap: '15px',
    marginTop: '30px',
    justifyContent: 'center', 
  },
  btn: {
    padding: '10px 30px',
    borderRadius: '6px',
    fontSize: '0.95rem',
    fontWeight: '600',
    cursor: 'pointer',
    border: 'none',
    minWidth: '100px',
    transition: 'opacity 0.2s',
  },
  btnPrimary: {
    backgroundColor: '#007bff', 
    color: 'white',
  },
  btnSecondary: {
    backgroundColor: '#6c757d', 
    color: 'white',
  },
};

export default UserRegistrationPage;